import React, { useState, useEffect } from 'react';
import API from '../utils/api';

const SkeletonBlock = ({ width = '100%', height = '20px', className = '' }) => (
  <div
    className={`rounded-3 ${className}`}
    style={{
      width,
      height,
      background: 'var(--header-bg)',
      animation: 'pulse 1.5s ease-in-out infinite',
    }}
  />
);

const growthStyles = `
  @keyframes pulse {
    0%, 100% { opacity: 0.6; }
    50% { opacity: 1; }
  }
`;

const GrowthDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    API.get('/stats/growth')
      .then((res) => {
        if (!cancelled) setData(res.data);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  if (error) return null;

  const personalBestDiff = data ? data.thisMonthStories - (data.lastMonthStories || 0) : 0;
  const topMilestone = data?.milestonesReached?.length
    ? data.milestonesReached[data.milestonesReached.length - 1]
    : null;

  return (
    <>
      <style>{growthStyles}</style>
      <div
        className="rounded-4 p-4 mb-4"
        style={{
          background: 'var(--card-bg)',
          border: '1px solid var(--border-purple)',
        }}
      >
        {loading ? (
          <div className="d-flex flex-column gap-3">
            <SkeletonBlock height="32px" width="60%" />
            <SkeletonBlock height="18px" width="80%" />
            <div className="d-flex gap-2">
              <SkeletonBlock height="28px" width="80px" />
              <SkeletonBlock height="28px" width="80px" />
              <SkeletonBlock height="28px" width="80px" />
            </div>
          </div>
        ) : data ? (
          <>
            <div className="d-flex align-items-center flex-wrap gap-3 mb-3">
              <div>
                <span
                  style={{
                    fontFamily: "'Fredoka One', cursive",
                    fontSize: '32px',
                    color: 'var(--text-main)',
                  }}
                >
                  📚 {data.totalStories || 0}
                </span>
                <span
                  style={{
                    fontSize: '14px',
                    color: 'var(--text-muted2)',
                    marginLeft: '6px',
                  }}
                >
                  stories together
                </span>
              </div>
              {data.familiesReadingThisWeek > 0 && (
                <div style={{ fontSize: '13px', color: 'var(--text-muted2)' }}>
                  🌙 {data.familiesReadingThisWeek} families reading this week
                </div>
              )}
            </div>

            {personalBestDiff > 0 && (
              <div
                className="rounded-3 px-3 py-2 mb-3 d-inline-block"
                style={{
                  background: 'var(--green-section)',
                  border: '1px solid var(--border-green)',
                  fontSize: '13px',
                  color: 'var(--text-green)',
                }}
              >
                📈 {personalBestDiff} more than last month — personal best!
              </div>
            )}

            {!personalBestDiff && data.lastMonthStories === 0 && data.thisMonthStories > 0 && (
              <div
                className="rounded-3 px-3 py-2 mb-3 d-inline-block"
                style={{
                  background: 'var(--green-section)',
                  border: '1px solid var(--border-green)',
                  fontSize: '13px',
                  color: 'var(--text-green)',
                }}
              >
                🌟 First month reading!
              </div>
            )}

            {topMilestone && (
              <div
                className="rounded-3 px-3 py-2 mb-3 d-inline-block"
                style={{
                  background: 'var(--yellow-section)',
                  border: '1px solid var(--border-yellow)',
                  fontSize: '13px',
                  color: 'var(--text-orange2)',
                }}
              >
                🎉 You've read {topMilestone} stories together!
              </div>
            )}

            {data.topThemes?.length > 0 && (
              <div className="d-flex flex-wrap gap-2 mt-2">
                {data.topThemes.slice(0, 4).map((t) => (
                  <span
                    key={t.name}
                    className="rounded-pill px-3 py-1"
                    style={{
                      background: 'var(--purple-section)',
                      color: 'var(--text-main)',
                      fontSize: '12px',
                      border: '1px solid var(--border-purple)',
                    }}
                  >
                    {t.name} {t.count > 1 ? `×${t.count}` : ''}
                  </span>
                ))}
              </div>
            )}
          </>
        ) : null}
      </div>
    </>
  );
};

export default GrowthDashboard;
